import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTextField } from 'src/components/mui-overrides/Textfield';
import { POST_WITH_ID } from '@brickwise/asset-validation/dist/asset/assetData';

import { getCoordinatesFromAddress, joinAddress } from 'src/util/utils';
import { useDebounceValue } from 'usehooks-ts';

interface IAddress {
  address: string;
  country: string;
  city: string;
  postalCode: string;
}

export const DEFAULT_COORDINATES = { lng: 48.214931, lat: 16.368166 };

export const calculateCoordinates = async ({ address, country, city, postalCode }: IAddress) => {
  const isAddressFilled = address !== '' && city !== '' && country !== '' && postalCode !== '';

  if (!isAddressFilled) {
    return DEFAULT_COORDINATES;
  }

  try {
    const newAddress = joinAddress(address, postalCode, city, country);
    const location = await getCoordinatesFromAddress(newAddress);
    const lat = location.lat();
    const lng = location.lng();

    return { lng, lat };
  } catch (e) {
    console.log(e);
    return DEFAULT_COORDINATES;
  }
};

const extractAddressFromForm = (values: POST_WITH_ID): IAddress => {
  return {
    address: values.location?.address || '',
    city: values.location?.city || '',
    country: values.location?.countryCode || '',
    postalCode: values.location?.postalCode || '',
  };
};

export const Coordinations = () => {
  const { t } = useTranslation();

  const formik = useFormikContext<POST_WITH_ID>();

  const address = useMemo(
    () => extractAddressFromForm(formik.values),
    [
      formik.values.location?.address,
      formik.values.location?.city,
      formik.values.location?.countryCode,
      formik.values.location?.postalCode,
    ],
  );

  const [debouncedAddress] = useDebounceValue(address, 500);

  useEffect(() => {
    const fetchCoordinates = async () => {
      const coordinates = await calculateCoordinates(debouncedAddress);
      formik.setFieldValue('location.coordinates.lat', coordinates.lat);
      formik.setFieldValue('location.coordinates.lng', coordinates.lng);
    };
    fetchCoordinates();
  }, [debouncedAddress]);

  return (
    <>
      <DefaultTextField
        disabled={true}
        formik={formik}
        label={t('dashboard.assets.addedit.main.generalinfo.fields.latitude.label')}
        fieldName="location.coordinates.lat"
      />
      <DefaultTextField
        disabled={true}
        formik={formik}
        label={t('dashboard.assets.addedit.main.generalinfo.fields.longitude.label')}
        fieldName="location.coordinates.lng"
      />
    </>
  );
};
